import { IAppContext } from 'App/AppContext';
import { TaskViewType } from 'components/Tasks/View/TasksView';
import {
  addDateDays,
  addDateMonths,
  addDateYears,
  fromApiDateOptional,
  getAuditStart,
  isValidDate,
} from 'utils/datetime';
import { enumParseValue } from 'utils/object';
import { IPeriodFilterData, PeriodFilterDataOption } from './PeriodFilter';

export interface IPeriod {
  start: Date | undefined;
  end: Date | undefined;
}

export const getPeriodFilter = (filterData: IPeriodFilterData, appContext: IAppContext): IPeriod => {
  if (!filterData) return { start: undefined, end: undefined };

  const customStart = fromApiDateOptional(filterData.customStart);
  const customEnd = fromApiDateOptional(filterData.customEnd);

  const start = getPeriodFilterStartDate(filterData.option, filterData.auditYear, customStart, appContext);
  const end = getPeriodFilterEndDate(filterData.option, filterData.auditYear, customEnd, appContext);

  return { start, end };
};

export const getPeriodFilterStartDate = (
  option: PeriodFilterDataOption | undefined,
  selectedAuditYear: number | undefined,
  customStart: Date | undefined,
  appContext: IAppContext,
): Date | undefined => {
  let start: Date | undefined = new Date();

  switch (option) {
    case PeriodFilterDataOption.LastWeek:
      start = addDateDays(start, -7);
      break;
    case PeriodFilterDataOption.LastMonth:
      start = addDateMonths(start, -1);
      break;
    case PeriodFilterDataOption.LastQuarter:
      start = addDateMonths(start, -3);
      break;
    case PeriodFilterDataOption.LastYear:
      start = addDateYears(start, -1);
      break;
    case PeriodFilterDataOption.AuditYear:
      start = getAuditStart(appContext, selectedAuditYear);
      break;
    case PeriodFilterDataOption.Custom:
      start = customStart ?? new Date();
      break;
    default:
      //PeriodFilterDataOption.All:
      start = undefined;
      break;
  }

  return start;
};

export const getPeriodFilterEndDate = (
  option: string | undefined,
  selectedAuditYear: number | undefined,
  customEnd: Date | undefined,
  appContext: IAppContext,
): Date | undefined => {
  let end: Date | undefined = new Date();

  switch (option) {
    case PeriodFilterDataOption.LastWeek:
    case PeriodFilterDataOption.LastMonth:
    case PeriodFilterDataOption.LastQuarter:
    case PeriodFilterDataOption.LastYear:
      end = new Date();
      break;
    case PeriodFilterDataOption.AuditYear:
      const start = getAuditStart(appContext, selectedAuditYear);
      end = addDateYears(start, 1);
      break;
    case PeriodFilterDataOption.Custom:
      end = customEnd ?? new Date();
      break;
    default:
      //PeriodFilterDataOption.All:
      end = undefined;
      break;
  }

  return end;
};

export const getPeriodFromURLParams = (baseParamName: string = 'period'): IPeriodFilterData | undefined => {
  const queryParams = new URLSearchParams(window.location.search);

  if (queryParams.has(baseParamName)) {
    const period = queryParams.get(baseParamName);

    const periodItems = period?.split(';');
    if (periodItems && periodItems.length > 0) {
      const periodData: IPeriodFilterData = {};
      periodItems.forEach((item) => {
        const itemData = item.split('@');
        if (itemData.length === 2) {
          const key = itemData[0];
          const value = itemData[1];
          switch (key) {
            case 'option':
              periodData.option = enumParseValue(PeriodFilterDataOption, value);
              if (periodData.option === undefined) {
                return undefined;
              }
              break;
            case 'auditYear':
              periodData.auditYear = Number(value);
              if (value && (isNaN(periodData.auditYear) || periodData.auditYear < 0)) {
                return undefined;
              }
              break;
            case 'customStart':
              periodData.customStart = value;
              if (periodData.customStart && !isValidDate(periodData.customStart)) {
                return undefined;
              }
              break;
            case 'customEnd':
              periodData.customEnd = value;
              if (periodData.customEnd && !isValidDate(periodData.customEnd)) {
                return undefined;
              }
              break;
            default:
              break;
          }
        }
      });

      return periodData;
    }
  }

  return undefined;
};

export const getPeriodURLParams = (period: IPeriodFilterData, baseParamName: string = 'period'): string => {
  let periodString = `${baseParamName}=`;
  if (period.option) {
    periodString += `option@${period.option};`;
  }
  if (period.auditYear) {
    periodString += `auditYear@${period.auditYear};`;
  }
  if (period.customStart) {
    periodString += `customStart@${period.customStart};`;
  }
  if (period.customEnd) {
    periodString += `customEnd@${period.customEnd};`;
  }

  return periodString;
};

export const getViewFromURLParams = (): TaskViewType | undefined => {
  const queryParams = new URLSearchParams(window.location.search);

  if (queryParams.has('view')) {
    const viewType = queryParams.get('view');
    switch (viewType) {
      case 'timeline':
      case TaskViewType.Timeline.toString():
        return TaskViewType.Timeline;
      case 'list':
      case TaskViewType.List.toString():
        return TaskViewType.List;
      case 'board':
      case TaskViewType.Board.toString():
        return TaskViewType.Board;
      case 'schedule':
      case TaskViewType.Schedule.toString():
        return TaskViewType.Schedule;
      default:
        break;
    }
  }

  return undefined;
};
